import { SOIL_SLOT, inPlot, type Plot } from "../render/plot";
import { fenceKey, edgeEnds, edgeCenter, parseFenceKey, FENCE_HALF_T, type FenceEdge } from "./fenceGeom";
import { waterCellAt, waterKey, PLOT_REFUND_RATE } from "./garden";
import { depthKey, DEPTH_EPS_POST_OWNER } from "../render/iso";
import { fenceStyle } from "./fenceStyles";

/**
 * FENCE SYSTEM — fences are SHORT picket segments placed on the slot sub-grid (render/plot.ts SOIL_SLOT):
 * they run along the BOUNDARIES between slots, one slot long each. Each fence auto-orients/connects to fences on the adjoining edges
 * (runs, corners and tees join automatically at shared posts).
 *
 * Geometry: a soil slot is CENTRED at (sx·SOIL_SLOT, sy·SOIL_SLOT) and spans ±SOIL_SLOT/2, so slot
 * BOUNDARY lines sit at HALF-slot offsets, (k+0.5)·SOIL_SLOT. Fence POSTS live on the lattice of slot
 * corners: post (px,py) is at garden ((px+0.5)·SOIL_SLOT, (py+0.5)·SOIL_SLOT). An edge is keyed by its
 * lower-index post + direction:
 *   • "h" = edge from post (sx,sy) to (sx, sy+1) — runs in +y (constant gx = a slot boundary).
 *   • "v" = edge from post (sx,sy) to (sx+1, sy) — runs in +x (constant gy = a slot boundary).
 */

const GRID = SOIL_SLOT;
// The lattice geometry lives in fenceGeom.ts (no game imports) so the placement rules can use it without
// an import cycle; re-exported here so every existing consumer keeps importing from sim/fence.
export { post, fenceKey, edgeKey, parseFenceKey, edgeEnds, edgeCenter, FENCE_HALF_T, fencesNear, discHitsFence } from "./fenceGeom";
export type { FenceDir, FenceEdge, FenceHit } from "./fenceGeom";

/** Coins to place one fence segment. */
export const FENCE_PRICE = 30;
/** Coins one DEMOLISHED segment gives back — the same haircut as selling a plot (`PLOT_REFUND_RATE`,
 *  75%), rounded per segment so a run's refund is always an exact multiple of the figure a player sees. */
export const FENCE_REFUND = Math.round(FENCE_PRICE * PLOT_REFUND_RATE);
/** A demolish drag that would take down MORE than this many segments asks first — one gesture can
 *  otherwise sweep away a whole run. */
export const FENCE_DEMOLISH_CONFIRM_OVER = 3;

/**
 * Snap a garden point to the nearest SHORT fence segment on a soil-slot boundary. The post lattice is at
 * half-slot offsets; we find the nearest post, then pick whichever of its 4 incident edges the point is
 * closest to (dominant axis + sign) — the same picking used for tile-edge snapping.
 */
export function nearestEdge(gx: number, gy: number): FenceEdge {
  // work in POST-lattice coords: post (px,py) is at (px+0.5)·GRID, so subtract the half offset.
  const fx = gx / GRID - 0.5, fy = gy / GRID - 0.5;
  const px = Math.round(fx), py = Math.round(fy);      // nearest post
  const dx = fx - px, dy = fy - py;                    // offset from that post (−0.5..0.5)
  if (Math.abs(dx) > Math.abs(dy)) {
    return dx >= 0 ? { sx: px, sy: py, dir: "v" } : { sx: px - 1, sy: py, dir: "v" };
  }
  return dy >= 0 ? { sx: px, sy: py, dir: "h" } : { sx: px, sy: py - 1, dir: "h" };
}

/**
 * For orientation: at a POST (lattice point p = (px,py)), which of the 4 incident edges have a fence?
 * Returns N/E/S/W booleans. Used by the renderer to draw the correct post/rail joins (straight/corner/tee)
 * and by nothing else — the model itself is just a set of edge keys.
 *   • West  edge of the post = the "v" edge of slot (px−1, py)
 *   • East  edge of the post = the "v" edge of slot (px,   py)
 *   • North edge of the post = the "h" edge of slot (px, py−1)
 *   • South edge of the post = the "h" edge of slot (px, py)
 */
export function postConnections(fences: ReadonlySet<string>, px: number, py: number): { n: boolean; e: boolean; s: boolean; w: boolean } {
  return {
    w: fences.has(fenceKey(px - 1, py, "v")),
    e: fences.has(fenceKey(px, py, "v")),
    n: fences.has(fenceKey(px, py - 1, "h")),
    s: fences.has(fenceKey(px, py, "h")),
  };
}

/** The key of the PLACED fence nearest to garden point (gx,gy), within `maxDist` garden units of the
 *  segment's midpoint, or null if none is close. Used by the demolish tool so hovering anywhere near a
 *  fence targets that whole section (not an empty adjacent edge). */
export function nearestPlacedFence(fences: ReadonlySet<string>, gx: number, gy: number, maxDist = GRID * 0.9): string | null {
  let best: string | null = null, bestD = maxDist * maxDist;
  for (const k of fences) {
    const e = parseFenceKey(k); if (!e) continue;
    const c = edgeCenter(e);
    const d = (c.gx - gx) ** 2 + (c.gy - gy) ** 2;
    if (d < bestD) { bestD = d; best = k; }
  }
  return best;
}

/** Defensive coerce of a persisted fence-key array. */
export function coerceFences(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const k of raw) {
    if (typeof k !== "string" || !parseFenceKey(k) || seen.has(k)) continue;
    seen.add(k); out.push(k);
  }
  return out;
}

/**
 * WHO DRAWS THE POST AT LATTICE POINT (px,py) — the single owner, as an edge key plus which HALF of it.
 *
 * Every lattice point with at least one incident fence must get exactly ONE post drawn, by the half that
 * sorts IN FRONT of it, and the renderer splits each segment into two halves that depth-sort separately
 * (see bakeFenceHalf). Depth is gx+gy and a post's two incident edges run +x ("v") and +y ("h"), so with
 * post P at depth d and a half sorting at its own MIDPOINT:
 *
 *   • P's EAST edge  (v at (px,py))   — P is its far end; that far half sorts at d + ¼ span → IN FRONT.
 *   • P's SOUTH edge (h at (px,py))   — P is its far end; that far half sorts at d + ¼ span → IN FRONT.
 *   • P's WEST edge  (v at (px−1,py)) — P is its near end; that near half sorts at d − ¼ span → BEHIND.
 *   • P's NORTH edge (h at (px,py−1)) — P is its near end; that near half sorts at d − ¼ span → BEHIND.
 *
 * So ownership goes to an E/S FAR half whenever one exists, and only falls back to a W/N near half when
 * the run arrives at P from behind and stops there (nothing sorts in front of P at all).
 *
 * THIS IS WHAT THE MISSING CORNER POST WAS. The old rule was "each half draws the post at its near end,
 * plus its outer post when arms <= 1", which hands P to its W/N halves — the ones BEHIND it. A corner
 * whose only arms are E and S (the up-screen corner of an enclosure) has no W or N edge, and its two far
 * halves were disqualified because arms === 2, so nobody drew it and the post was simply absent.
 *
 * E is preferred over S only to make the choice deterministic: the two far halves sort at the same depth,
 * so without a fixed rule the winner would depend on child insertion order and could change on a rebuild.
 *
 * Returns null when no fence touches the point.
 */
export function postOwner(
  fences: ReadonlySet<string>, px: number, py: number,
): { key: string; half: "far" | "near" } | null {
  const c = postConnections(fences, px, py);
  // In front of P (P is the edge's FAR end, so its far half spans P → centre).
  if (c.e) return { key: fenceKey(px, py, "v"), half: "far" };
  if (c.s) return { key: fenceKey(px, py, "h"), half: "far" };
  // Behind P, but nothing in front exists — the run terminates here.
  if (c.w) return { key: fenceKey(px - 1, py, "v"), half: "near" };
  if (c.n) return { key: fenceKey(px, py - 1, "h"), half: "near" };
  return null;
}

/**
 * WHO DRAWS THE WALL STYLES' BLOCK AT (px,py) — the pier or clipped hedge end — and whether it goes down BEFORE
 * or AFTER that half's own wall. Not the post owner: a post is thinner than everything that meets it and must
 * sort in front of all of it, but a block is a solid the walls stand against. A wall leaving it TOWARD the viewer
 * (an E or S arm, sorting at P + ¼ span) covers the foot of its face; a wall leaving it AWAY (W or N, at P − ¼) is
 * covered by it. So the block goes between them: drawn first by the front half that sorts first — the S far half,
 * which carries no depth bias unless it owns the post, and only owns it when there is no E arm (fenceHalfDepth) —
 * else the E far half; with no front arm, last, by the post owner after its own wall.
 *
 * Drawn by the post owner after its wall regardless, the block painted over every front arm: each wall seemed to
 * slide in behind the pier's far edge half-way up instead of standing out of its face, and a hedge corner showed a
 * pillar seam through what should be one mass.
 */
export function blockOwner(
  fences: ReadonlySet<string>, px: number, py: number,
): { key: string; half: "far" | "near"; order: "before" | "after" } | null {
  const c = postConnections(fences, px, py);
  if (c.s) return { key: fenceKey(px, py, "h"), half: "far", order: "before" };
  if (c.e) return { key: fenceKey(px, py, "v"), half: "far", order: "before" };
  const own = postOwner(fences, px, py);
  return own ? { ...own, order: "after" } : null;
}

/**
 * THE SORT KEY OF ONE FENCE HALF — the number both fence renderers (GardenGroundLayer.drawFences and the
 * preview's fenceArt.drawFenceRun) sort that half by: `depth` is the gx+gy of the half's own midpoint
 * (outer end → segment centre), `bias` is DEPTH_EPS_POST_OWNER when this half owns its outer post, half that
 * for any other "v" half, else 0.
 *
 * THE BIAS IS THE CORNER FIX. At an E+S corner both far halves sit at exactly P + ¼ span; at a W+N corner
 * both near halves at P − ¼ span; tees and crosses tie the same way. With equal keys the draw order fell
 * to child insertion order — so whichever half was added last painted its rails, boards, wall or hedge
 * straight across the corner post the other half owns, and a re-bake (which re-adds a node at the end)
 * could flip a corner's look after an edit next door. The owner now always sorts last at its post. The half
 * step for "v" halves settles the one tie left: at an S+W+N tee or a cross the W and N near halves both sit
 * behind the post at the same depth, and their edges cross just beside it (visible on box hedge).
 * The depth solve moves `depth` and re-adds `bias` after, so a clamp cannot flatten the pair back to a tie.
 *
 * Within one style all of that comes to "the v half wins its side's tie" — and that is only right because one
 * style draws one post at a join and its art is built for it. Where the tie is between TWO STYLES (`styleOf`;
 * without it every segment is one style), each caps its own end at the point, and `mixedTieWinner` decides.
 */
export function fenceHalfDepth(
  fences: ReadonlySet<string>, key: string, which: "far" | "near", styleOf?: (key: string) => string,
): { depth: number; bias: number } {
  const e = parseFenceKey(key);
  if (!e) return { depth: 0, bias: 0 };
  const [far, near] = edgeEnds(e);           // edgeEnds lists the far (smaller gx+gy) post first
  const end = which === "far" ? far : near, m = edgeCenter(e);
  const px = which === "far" ? e.sx : e.dir === "v" ? e.sx + 1 : e.sx;
  const py = which === "far" ? e.sy : e.dir === "h" ? e.sy + 1 : e.sy;
  const depth = depthKey((end.gx + m.gx) / 2, (end.gy + m.gy) / 2);
  const mixed = styleOf ? mixedTieWinner(fences, styleOf, px, py, which === "far" ? "front" : "back") : null;
  if (mixed) return { depth, bias: mixed === e.dir ? DEPTH_EPS_POST_OWNER : 0 };
  const own = postOwner(fences, px, py);
  return {
    depth,
    bias: own?.key === key && own.half === which ? DEPTH_EPS_POST_OWNER : e.dir === "v" ? DEPTH_EPS_POST_OWNER / 2 : 0,
  };
}

/**
 * WHERE TWO STYLES TIE AT A POST, WHICH DRAWS LAST — "v" or "h", or null when the side holds no tie between two
 * different styles. A side is the pair of halves at post P on one side of it in depth: FRONT = the E and S far
 * halves (both at P + ¼ span), BACK = the W and N near halves (both at P − ¼). Each half carries its own style's
 * cap at P, so the winner's cap and arm-end paint over the loser's.
 *
 *  1. A STYLE THAT RUNS STRAIGHT THROUGH P is one wall/fence line, and an arm of another style that BUTTS into it
 *     lies wholly on one side of that line: from the front (an S arm against a W–E line, an E arm against N–S)
 *     it is in front of the line and draws after it; from behind (N against W–E, W against N–S) it draws before.
 *     The old blanket "v wins" got exactly half of these backwards: a stone or rail run butting into the FRONT of
 *     a W–E hedge went behind the hedge's E half — the rail's end post vanished into the hedge and a stone pier's
 *     corner poked out of it — and a hedge butting into the front of a W–E rail line had the rails and the post
 *     drawn across its end.
 *  2. Otherwise (an L of two styles, or two lines crossing) it goes by how BULKY each style's cap is (fenceStyles
 *     `cap`: an iron post, bamboo stake, picket post, rail post, then the hedge block and the stone pier). In FRONT
 *     of P both arms come toward the viewer, and the SLIMMER cap draws last, so it stands at the face of the
 *     bulkier one instead of buried in it — a rail corner post turning beside a hedge end was hidden but for its
 *     top, and an iron corner post vanished behind a rail post but for its finial. BEHIND P both arms run away,
 *     and the BULKIER cap draws last: it stands in front of the other arm's last stretch, which otherwise crossed
 *     its face (a rail run arriving from behind drew its rails across the front of a stone pier).
 */
export function mixedTieWinner(
  fences: ReadonlySet<string>, styleOf: (key: string) => string, px: number, py: number, side: "front" | "back",
): "v" | "h" | null {
  const vKey = side === "front" ? fenceKey(px, py, "v") : fenceKey(px - 1, py, "v");       // E / W
  const hKey = side === "front" ? fenceKey(px, py, "h") : fenceKey(px, py - 1, "h");       // S / N
  if (!fences.has(vKey) || !fences.has(hKey)) return null;
  const vs = styleOf(vKey), hs = styleOf(hKey);
  if (vs === hs) return null;
  // does each style continue STRAIGHT through P, on the arm opposite this one?
  const vOpp = side === "front" ? fenceKey(px - 1, py, "v") : fenceKey(px, py, "v");       // W / E
  const hOpp = side === "front" ? fenceKey(px, py - 1, "h") : fenceKey(px, py, "h");       // N / S
  const vLine = fences.has(vOpp) && styleOf(vOpp) === vs;
  const hLine = fences.has(hOpp) && styleOf(hOpp) === hs;
  if (vLine !== hLine) {
    // the butting arm is the one that is NOT a line: after the line in front of P, before it behind
    const butting = vLine ? "h" : "v";
    return side === "front" ? butting : butting === "v" ? "h" : "v";
  }
  const slim = fenceStyle(vs).cap < fenceStyle(hs).cap ? "v" : "h";
  return side === "front" ? slim : slim === "v" ? "h" : "v";
}

/**
 * CAN A FENCE BE BUILT ON THIS EDGE? Both posts must be inside the plot, and no water may touch the
 * span between them.
 *
 * `nearestEdge` snaps on an INFINITE lattice — it always returns an edge, however far outside the
 * garden the click was — so without this the fence tool was the one placement tool with no bounds:
 * segments could be bought standing in the void off the plot's corner or straight through the pond,
 * and the renderer drew them there quite happily. Every other tool (till, plant, decor) clips to the
 * plot, and the fence now matches.
 *
 * WATER TEST: an edge is a LINE, not a box, so its two endpoints already bracket the whole strip of
 * water cells it passes over — an axis-aligned span from post to post covers exactly the inclusive
 * index range between them. Same sweep `canTillSlot` does for a soil slot's box, one row wide.
 * Endpoints INCLUSIVE on purpose: a fence whose post lands on the pond edge is standing in the water.
 */
export function canBuildFence(
  plot: Plot, e: FenceEdge, water: ReadonlySet<string>, obstacles?: readonly FenceObstacle[],
): boolean {
  return fenceBuildBlock(plot, e, water, obstacles) === null;
}

/** Something standing on the ground that a new fence may not be built through: a placed plant or decor
 *  item, as its centre and its collision radius (garden units). */
export interface FenceObstacle { gx: number; gy: number; r: number; }

/** canBuildFence's rules, naming the FIRST one broken — "bounds" (off the plot or in the water) or
 *  "blocked" (an obstacle stands on the line) — or null when the edge is clear.
 *
 *  THE OBSTACLE TEST is the placement rules' fence test run the other way round (garden.ts canPlaceAt /
 *  decorPlaceBlock refuse a footprint within FENCE_HALF_T of a standing fence), so a fence and a footprint
 *  can never share ground whichever of the two came first. That is what keeps an item from standing
 *  THROUGH a fence: there is no 2D draw order that is right for an object straddling the line. */
export function fenceBuildBlock(
  plot: Plot, e: FenceEdge, water: ReadonlySet<string>, obstacles?: readonly FenceObstacle[],
): "bounds" | "blocked" | null {
  const [a, b] = edgeEnds(e);
  if (!inPlot(plot, a.gx, a.gy, 0) || !inPlot(plot, b.gx, b.gy, 0)) return "bounds";
  const c0 = waterCellAt(a.gx, a.gy), c1 = waterCellAt(b.gx, b.gy);
  for (let wx = Math.min(c0.wx, c1.wx); wx <= Math.max(c0.wx, c1.wx); wx++) {
    for (let wy = Math.min(c0.wy, c1.wy); wy <= Math.max(c0.wy, c1.wy); wy++) {
      if (water.has(waterKey(wx, wy))) return "bounds";
    }
  }
  if (obstacles) for (const o of obstacles) {
    const nx = Math.min(Math.max(o.gx, Math.min(a.gx, b.gx)), Math.max(a.gx, b.gx));
    const ny = Math.min(Math.max(o.gy, Math.min(a.gy, b.gy)), Math.max(a.gy, b.gy));
    const rr = o.r + FENCE_HALF_T;
    if ((o.gx - nx) ** 2 + (o.gy - ny) ** 2 < rr * rr) return "blocked";
  }
  return null;
}
